import {
  Badge,
  Box,
  Button,
  Flex,
  Text,
  HStack,
  Icon,
  VStack,
  ButtonProps,
} from "@chakra-ui/react";
import React from "react";
import { AnimatedChevronIcon } from "../../../AnimatedChevronIcon";
import { ImportMethodOption } from "../helpers/getImportMethodsAvailable";
import { ImportMethod } from "../panels/ImportMethodSelectionPanel";

type ImportMethodButtonProps = {
  option: ImportMethodOption;
  recommended: boolean;
  onSelect: (importType: ImportMethod) => void;
  onKeyDown?: React.KeyboardEventHandler<HTMLButtonElement>;
  buttonRef?: React.Ref<HTMLButtonElement>;
  buttonProps?: ButtonProps;
};

export function ImportMethodButton({
  option,
  recommended,
  onSelect,
  onKeyDown,
  buttonRef,
  buttonProps,
}: ImportMethodButtonProps): React.ReactElement {
  return (
    <Button
      ref={buttonRef}
      role="group"
      variant="outline"
      colorScheme={option.colorScheme}
      w="full"
      h="auto"
      justifyContent="flex-start"
      whiteSpace="normal"
      textAlign="left"
      shadow="sm"
      px={4}
      py={4}
      onClick={() => onSelect(option.importType)}
      onKeyDown={onKeyDown}
      {...buttonProps}
    >
      <HStack w="full" align="flex-start" spacing={4}>
        <ImportMethodIcon option={option} />

        <ImportMethodContent option={option} recommended={recommended} />

        <VStack alignSelf="center">
          <AnimatedChevronIcon color={`${option.colorScheme}.500`} />
        </VStack>
      </HStack>
    </Button>
  );
}

function ImportMethodIcon({
  option,
}: {
  option: ImportMethodOption;
}): React.ReactElement {
  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      boxSize="2.25rem"
      borderRadius="full"
      bg={`${option.colorScheme}.50`}
      flexShrink={0}
    >
      <Icon as={option.icon} boxSize={4} />
    </Box>
  );
}

function ImportMethodContent({
  option,
  recommended = false,
}: {
  option: ImportMethodOption;
  recommended?: boolean;
}): React.ReactElement {
  return (
    <VStack align="flex-start" spacing={2} flex={1} minW={0}>
      <Flex align="flex-start" wrap="wrap" gap={2}>
        <Text fontWeight="bold" color="gray.800">
          {option.label}
        </Text>

        {recommended && (
          <Badge colorScheme={option.colorScheme}>Recommended</Badge>
        )}
      </Flex>

      <Text
        fontSize="sm"
        fontWeight="normal"
        color="gray.800"
        lineHeight="normal"
      >
        {option.description}
      </Text>
    </VStack>
  );
}
